import { useEffect, useState } from 'react';
import { supabase } from '../utils/supabaseClient';

/**
 * Lightweight hook that queries referral stats for the current user (as inviter).
 *
 * Returns:
 *   monthlyCount – paid referrals in the current UTC month (cap display: X / 5)
 *   totalCount   – all-time paid referrals
 *   loading      – true while the initial fetch is in-flight
 *
 * Relies on RLS: users can SELECT their own referral rows.
 */
const useReferralStats = (userId) => {
  const [monthlyCount, setMonthlyCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchStats = async () => {
      try {
        // First moment of the current UTC month
        const now = new Date();
        const monthStart = new Date(
          Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1),
        );

        const [totalResult, monthlyResult] = await Promise.all([
          // All-time paid referrals
          supabase
            .from('referrals')
            .select('id', { count: 'exact', head: true })
            .eq('inviter_id', userId)
            .eq('status', 'paid'),

          // Paid referrals this UTC month
          supabase
            .from('referrals')
            .select('id', { count: 'exact', head: true })
            .eq('inviter_id', userId)
            .eq('status', 'paid')
            .gte('inviter_paid_at', monthStart.toISOString()),
        ]);

        if (cancelled) return;

        setTotalCount(totalResult.count ?? 0);
        setMonthlyCount(monthlyResult.count ?? 0);
      } catch {
        // Non-critical — stats display 0 on failure
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchStats();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  return { monthlyCount, totalCount, loading };
};

export default useReferralStats;
